import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, UserPlus, UserMinus, Ban, MessageCircle, Check, Loader2, Star } from "lucide-react";
import { api } from "@/lib/api";
import { Avatar } from "@/components/Avatar";

export const UserModal = ({ userId, open, onClose, onMessage }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = () => {
    if (!userId) return;
    setLoading(true);
    api
      .get(`/users/${userId}`)
      .then((r) => setData(r.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    if (open && userId) load();
  }, [open, userId]);

  const act = async (fn) => {
    await fn();
    load();
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          data-testid="user-modal"
          className="fixed inset-0 z-[90] flex items-center justify-center p-5"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
        >
          <div className="absolute inset-0 bg-black/80 backdrop-blur-md" onClick={onClose} />
          <motion.div
            initial={{ opacity: 0, scale: 0.9, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.92 }}
            transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
            className="electric-card relative z-10 w-full max-w-md rounded-2xl bg-black/75 backdrop-blur-2xl border border-white/10 p-8"
          >
            <button
              onClick={onClose}
              data-testid="user-modal-close"
              className="absolute top-4 right-4 w-9 h-9 rounded-full bg-black/60 border border-white/10 flex items-center justify-center text-slate-400 hover:text-cyan-400"
            >
              <X className="w-4 h-4" />
            </button>

            {loading || !data ? (
              <div className="flex justify-center py-10">
                <Loader2 className="w-6 h-6 text-cyan-400 animate-spin" />
              </div>
            ) : (
              <>
                <div className="flex items-center gap-4">
                  <Avatar path={data.avatar_path} name={data.nickname} size="lg" online={data.online} />
                  <div>
                    <h3 className="font-heading font-extrabold text-xl text-white inline-flex items-center gap-2" data-testid="user-modal-nickname">
                      {data.nickname}
                      {data.is_admin && <Star data-testid="user-modal-admin-star" className="w-5 h-5 text-yellow-400 fill-yellow-400" />}
                    </h3>
                    {data.banned && <span className="text-xs text-red-400">banned</span>}
                  </div>
                </div>

                {data.bio && <p className="mt-5 text-sm text-slate-300 leading-relaxed">{data.bio}</p>}

                {data.links?.length > 0 && (
                  <div className="mt-4 flex flex-wrap gap-2">
                    {data.links.map((l, i) => (
                      <a
                        key={i}
                        href={l.url}
                        target="_blank"
                        rel="noreferrer"
                        className="btn-ghost-electric rounded-full border border-white/15 px-3 py-1.5 text-xs font-semibold text-cyan-300"
                      >
                        {l.label}
                      </a>
                    ))}
                  </div>
                )}

                <div className="mt-7 flex flex-wrap gap-3">
                  {data.is_blocked ? (
                    <button
                      data-testid="user-unblock-button"
                      onClick={() => act(() => api.delete(`/block/${userId}`))}
                      className="inline-flex items-center gap-2 rounded-full border border-white/15 px-4 py-2.5 text-sm font-semibold text-white hover:border-cyan-400/50"
                    >
                      Unblock
                    </button>
                  ) : (
                    <>
                      {data.is_friend ? (
                        <button
                          data-testid="user-unfriend-button"
                          onClick={() => act(() => api.delete(`/friends/${userId}`))}
                          className="inline-flex items-center gap-2 rounded-full border border-white/15 px-4 py-2.5 text-sm font-semibold text-white hover:border-red-400/50 hover:text-red-400"
                        >
                          <UserMinus className="w-4 h-4" /> Remove friend
                        </button>
                      ) : data.request_received ? (
                        <button
                          data-testid="user-accept-button"
                          onClick={() => act(() => api.post(`/friends/accept/${userId}`))}
                          className="btn-electric inline-flex items-center gap-2 text-black font-bold rounded-full px-4 py-2.5 text-sm"
                        >
                          <Check className="w-4 h-4" /> Accept request
                        </button>
                      ) : data.request_sent ? (
                        <button
                          disabled
                          className="inline-flex items-center gap-2 rounded-full border border-white/15 px-4 py-2.5 text-sm font-semibold text-slate-500"
                        >
                          Request sent
                        </button>
                      ) : (
                        <button
                          data-testid="user-addfriend-button"
                          onClick={() => act(() => api.post(`/friends/request/${userId}`))}
                          className="btn-electric inline-flex items-center gap-2 text-black font-bold rounded-full px-4 py-2.5 text-sm"
                        >
                          <UserPlus className="w-4 h-4" /> Add friend
                        </button>
                      )}

                      <button
                        data-testid="user-message-button"
                        onClick={() => {
                          onClose();
                          onMessage?.(userId);
                        }}
                        className="inline-flex items-center gap-2 rounded-full border border-white/15 px-4 py-2.5 text-sm font-semibold text-white hover:border-cyan-400/50"
                      >
                        <MessageCircle className="w-4 h-4" /> Message
                      </button>

                      <button
                        data-testid="user-block-button"
                        onClick={() => act(() => api.post(`/block/${userId}`))}
                        className="inline-flex items-center gap-2 rounded-full border border-white/15 px-4 py-2.5 text-sm font-semibold text-slate-400 hover:border-red-400/50 hover:text-red-400"
                      >
                        <Ban className="w-4 h-4" /> Block
                      </button>
                    </>
                  )}
                </div>
              </>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
